import { Component, createRef } from "react";
import { IBaseComponentProps, NSBoxBaseCombo, NSBoxDouble, NSBoxEnum, NSRow } from "namirasoft-site-react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { BillWatchProps } from "../BillWatchProps";
import { NamingConvention } from "namirasoft-core";
import { ChangeType, FormulaRow, FormulaDefinitionRow, Formulas } from "namirasoft-bill-watch";

export interface ConditionLineProps extends IBaseComponentProps, NSARouterMakerResult, BillWatchProps
{
    index: number | null;
    deletable: boolean;
    fullWidth?: boolean;
    onDelete: () => void;
    onChanged?: (value: any) => void;
    required: boolean;
    nullable: boolean;
}

interface ConditionLineState
{
    formula: FormulaDefinitionRow | null;
}

export default class ConditionLine extends Component<ConditionLineProps, ConditionLineState>
{
    static IS_DIRTY(value: FormulaRow | null): boolean
    {
        if (value)
            if (value.name)
                return true;
        return false;
    }

    private NSBoxBaseCombo_Condition = createRef<NSBoxBaseCombo<any>>();
    private NSBoxEnum_Change = createRef<NSBoxEnum<ChangeType>>();

    private NSBoxDouble_Ps: { [name: string]: React.RefObject<NSBoxDouble> } = {};

    private PendingValue: FormulaRow | null = null;

    constructor(props: ConditionLineProps)
    {
        super(props);

        this.state = { formula: null };

        this.getValue = this.getValue.bind(this);
        this.isDirty = this.isDirty.bind(this);
        this.onChanged = this.onChanged.bind(this);
    }

    override componentDidUpdate(_: Readonly<ConditionLineProps>, prevState: Readonly<ConditionLineState>)
    {
        if (this.state.formula !== prevState.formula && this.PendingValue)
        {
            let ps = Object.keys(this.NSBoxDouble_Ps);
            ps.forEach(p =>
            {
                this.NSBoxDouble_Ps[p].current?.setValue(this.PendingValue!.parameters[p]);
            });
            this.PendingValue = null;
        }
    }

    getValue(checkError?: boolean): FormulaRow
    {
        let name = this.NSBoxBaseCombo_Condition.current?.getValueOne(checkError) ?? "";
        let change = this.NSBoxEnum_Change.current?.getValueEnumOne(ChangeType, ChangeType.Increased, checkError) ?? ChangeType.Increased;
        let ps = Object.keys(this.NSBoxDouble_Ps);
        let parameters: { [name: string]: number } = {};
        ps.forEach(p =>
        {
            parameters[p] = this.NSBoxDouble_Ps[p].current?.getValue() ?? 0;
        })
        return { name, change, parameters }
    }

    setValue(value: FormulaRow)
    {
        this.NSBoxBaseCombo_Condition.current?.setValue(value.name, () =>
        {
            this.NSBoxEnum_Change.current?.setValueOne(value.change);
            this.PendingValue = value;
        });
    }

    isDirty(): boolean
    {
        return ConditionLine.IS_DIRTY(this.getValue(false));
    }

    private onChanged()
    {
        if (this.props.onChanged)
            this.props.onChanged(this.getValue(false));
    }

    override render()
    {
        let parameter_names: string[] = [];
        if (this.state.formula?.parameters)
            parameter_names = Object.keys(this.state.formula?.parameters);

        return (
            <NSRow
                style={{ justifyContent: "start" }}
            >
                <NSBoxBaseCombo<FormulaDefinitionRow>
                    ref={this.NSBoxBaseCombo_Condition}
                    {...this.props}
                    title={`Anomaly Formula ${this.props.index != null ? (this.props.index + 1) : ""}`}
                    getItems={async () =>
                    {
                        return { count: Formulas.ALL.length, rows: Formulas.ALL };
                    }}
                    getTitle={(item) => NamingConvention.auto.convert(item.name, NamingConvention.Pascal_Case_Space)}
                    getValue={(item) => item.name}
                    getSort={() => null}
                    required={this.props.required}
                    defaultValue={this.props.nullable ? undefined : Formulas.ALL[0].name}
                    multiple={false}
                    onChanged={(e) =>
                    {
                        let formula = e.getDataOne();
                        if (formula)
                        {
                            this.NSBoxDouble_Ps = {};
                            Object.keys(formula.parameters).forEach(p =>
                            {
                                this.NSBoxDouble_Ps[p] = createRef<NSBoxDouble>();
                            });
                            this.setState({ formula });
                        }
                        this.onChanged();
                    }}
                    style={{ ...this.props.style }}
                    menu={{
                        builtin: {
                            delete: {
                                enabled: this.props.deletable,
                                onDelete: () => { this.props.onDelete(); }
                            }
                        },
                        items: []
                    }}
                />
                <NSBoxEnum<ChangeType>
                    ref={this.NSBoxEnum_Change}
                    title="Change Type"
                    required
                    defaultValue={ChangeType.Increased}
                    multiple={false}
                    getEnumObject={() => ChangeType}
                ></NSBoxEnum>
                {
                    parameter_names.map((p, index) => (
                        <NSBoxDouble
                            key={index + 1}
                            ref={this.NSBoxDouble_Ps[p]}
                            required
                            title={NamingConvention.auto.convert(p, NamingConvention.Pascal_Case_Space)}
                            defaultValue={this.state.formula?.parameters[p].default}
                            min={this.state.formula?.parameters[p].min ?? undefined}
                            max={this.state.formula?.parameters[p].max ?? undefined}
                        />))
                }
                {
                    this.state.formula &&
                    <>
                        <p
                            style={{ color: "black" }}
                        >{this.state.formula?.description}</p>
                        <a href={this.state.formula?.learn_more} target="_blank" rel="noreferrer" >Learn More!</a>
                    </>
                }
            </NSRow>
        );
    }
}